import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { fetchProducts, ProductFilterOptions } from '@/services/productService';
import { fetchCategoryById, fetchCategories, fetchSubcategories } from '@/services/categoryService';
import ProductCard from '@/components/product/ProductCard';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Category, Subcategory } from '@/types/category';
import { Product } from '@/types/product';
import { useLanguage } from '@/context/LanguageContext';
import { Separator } from "@/components/ui/separator";

const CategoryPage = () => {
  const { id } = useParams<{ id: string }>();
  const [sort, setSort] = useState('newest');
  const navigate = useNavigate();
  const { t, language, tl } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('all');
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  
  // Fetch all categories
  const { data: allCategories } = useQuery({
    queryKey: ['categories'],
    queryFn: fetchCategories,
  });
  
  // Find category by name/slug
  const findCategory = (categoryIdentifier: string, categories: Category[]) => {
    // First try to find by exact name match
    const exactMatch = categories?.find(cat => 
      cat.name.toLowerCase() === categoryIdentifier.toLowerCase()
    );
    
    if (exactMatch) return exactMatch;
    
    // If no exact match, try to find by slug or hyphenated name
    const normalizedIdentifier = categoryIdentifier.toLowerCase().replace(/-/g, ' ');
    return categories?.find(cat => 
      cat.name.toLowerCase() === normalizedIdentifier ||
      cat.slug?.toLowerCase() === categoryIdentifier.toLowerCase()
    );
  };
  
  // Get the category
  const category = allCategories ? findCategory(id as string, allCategories) : null;

  // Fetch subcategories when category changes
  useEffect(() => {
    const loadSubcategories = async () => {
      if (category) {
        try {
          const subcategoriesData = await fetchSubcategories(category.id);
          setSubcategories(subcategoriesData);
        } catch (error) {
          console.error('Error loading subcategories:', error);
        }
      }
    };
    loadSubcategories();
  }, [category]);
  
  // Fetch products for the category
  const {
    data: products = [],
    isLoading: productsLoading,
    error: productsError
  } = useQuery({
    queryKey: ['categoryProducts', category?.name, sort, selectedSubcategory, searchQuery],
    queryFn: async () => {
      if (!category) return [];
      const filterOptions: ProductFilterOptions = {
        categories: [category.name],
        sort,
        subcategory: selectedSubcategory !== 'all' ? selectedSubcategory : undefined,
        search: searchQuery || undefined,
      };
      return fetchProducts(filterOptions);
    },
    enabled: !!category
  });
  
  // Format for meta title and description
  const metaTitle = category ? `${category.name} | Meubles Karim` : 'Catégorie | Meubles Karim';
  const metaDescription = category ? 
    `Explorez notre collection de ${category.name}. Des meubles de qualité pour votre maison chez Meubles Karim.` : 
    'Parcourez nos collections de meubles par catégorie chez Meubles Karim.';

  // Group products by subcategory
  const groupedProducts = products.reduce((acc, product) => {
    const subcategory = product.subcategory || 'Uncategorized';
    if (!acc[subcategory]) {
      acc[subcategory] = [];
    }
    acc[subcategory].push(product);
    return acc;
  }, {} as Record<string, Product[]>);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleSubcategoryChange = (value: string) => {
    setSelectedSubcategory(value);
  };
  
  if (productsLoading || (allCategories && !category)) {
    return <CategorySkeleton />;
  }
  
  if (productsError || !category) {
    return (
      <div className="container-custom py-16 text-center bg-white ">
        <h2 className="text-2xl font-medium mb-4 text-black">
          {tl('Catégorie Introuvable', 'Category Not Found', 'Categoria non trovata')}
        </h2>
        <p className="text-muted-foreground mb-8 text-black">
          {tl(
            "La catégorie que vous recherchez n'existe pas ou a été supprimée.",
            'The category you are looking for does not exist or has been removed.',
            'La categoria che state cercando non esiste o è stata rimossa.'
          )}
        </p>
        <Link 
          to="/" 
          className="inline-flex items-center text-furniture-taupe hover:text-furniture-brown"
        >
          <ArrowLeft size={16} className="mr-2" />
          {tl("Retour à l'accueil", "Back to Home", "Torna alla home")}
        </Link>
      </div>
    );
  }

  return (
    <main className="bg-white text-black">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
      </Helmet>
      
      {/* Hero Section */}
      <section className="relative h-[50vh] overflow-hidden">
        {/* Hero Background Image */}
        {category.image ? (
          <div className="absolute inset-0 z-0">
            <img 
              src={category.image}
              alt={category.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30"></div>
          </div>
        ) : (
          <div className="absolute inset-0 z-0 bg-gradient-to-br from-gray-100 to-gray-200"></div>
        )}
        
        {/* Content */}
        <div className="container-custom relative z-10 h-full flex flex-col justify-center">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className={`font-sans text-4xl md:text-5xl lg:text-6xl font-semibold mb-6 ${
              category.image ? 'text-white' : 'text-gray-900'
            }`}>
              {category.name}
            </h1>
            {category.description && (
              <p className={`text-lg leading-relaxed max-w-2xl mx-auto ${
                category.image ? 'text-white/90' : 'text-gray-600'
              }`}>
                {category.description}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Filters and Search Row */}
      <div className="flex flex-col items-center gap-4 bg-white" style={{ backgroundColor: 'white !important', marginTop: '0 !important' }}>
        <div className="flex flex-row gap-4 w-full max-w-4xl justify-center bg-white text-black border border-gray-200 rounded-lg p-4" style={{ backgroundColor: 'white !important' }}>
          {/* Subcategories Dropdown */}
          <Select value={selectedSubcategory} onValueChange={handleSubcategoryChange}>
            <SelectTrigger className="w-[200px] bg-white text-black border-gray-300" style={{ backgroundColor: 'white !important', color: 'black !important' }}>
              <SelectValue placeholder={tl('Sous-catégories', 'Subcategories', 'Sottocategorie')} />
            </SelectTrigger>
            <SelectContent className="bg-white border-gray-300">
              <SelectItem value="all" className="bg-white text-black hover:bg-gray-100">
                {tl('Toutes les sous-catégories', 'All Subcategories', 'Tutte le sottocategorie')}
              </SelectItem>
              {subcategories.map(subcategory => (
                <SelectItem key={subcategory.id} value={subcategory.id} className="bg-white text-black hover:bg-gray-100">
                  {subcategory.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Sort Dropdown */}
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[200px] bg-white text-black border-gray-300" style={{ backgroundColor: 'white !important', color: 'black !important' }}>
              <SelectValue placeholder={tl('Trier par', 'Sort by', 'Ordina per')} />
            </SelectTrigger>
            <SelectContent className="bg-white border-gray-300">
              <SelectItem value="newest" className="bg-white text-black hover:bg-gray-100">
                {tl('Plus récent', 'Newest', 'Più recenti')}
              </SelectItem>
              <SelectItem value="price-asc" className="bg-white text-black hover:bg-gray-100">
                {tl("Prix: Bas à Haut", "Price: Low to High", "Prezzo: dal basso all'alto")}
              </SelectItem>
              <SelectItem value="price-desc" className="bg-white text-black hover:bg-gray-100">
                {tl("Prix: Haut à Bas", "Price: High to Low", "Prezzo: dall'alto al basso")}
              </SelectItem>
              <SelectItem value="rating" className="bg-white text-black hover:bg-gray-100">
                {tl('Mieux notés', 'Top Rated', 'Più votati')}
              </SelectItem>
            </SelectContent>
          </Select>

          {/* Search Bar */}
          <Input
            type="text"
            placeholder={tl('Rechercher...', 'Search products', 'Cerca...')}
            value={searchQuery}
            onChange={handleSearchChange}
            className="w-[250px] bg-white text-black border-gray-300"
            style={{ backgroundColor: 'white !important', color: 'black !important' }}
          />
        </div>
      </div>
      
      {/* Products Display */}
      <div className="container bg-white text-black mx-auto px-4 pb-16" style={{ backgroundColor: 'white !important', marginTop: '0 !important' }}>
        {products.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-lg text-muted-foreground">
              {tl(
                'Aucun produit trouvé dans cette catégorie.',
                'No products found in this category.',
                'Nessun prodotto trovato in questa categoria.'
              )}
            </p>
            <Button variant="outline" className="mt-4" asChild>
              <Link to="/products">
                {tl('Parcourir tous les produits', 'Browse all products', 'Sfoglia tutti i prodotti')}
              </Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-16">
            {Object.entries(groupedProducts).map(([subcategoryId, products]) => {
              const subcategory = subcategories.find(s => s.id === subcategoryId);
              const headerLabel = subcategory?.name || 
                (subcategoryId === 'Uncategorized' 
                  ? ' '
                  : subcategoryId);
              
              return (
                <div key={subcategoryId} className="space-y-6 mb-12">
                  <h2
                    className="text-2xl font-medium text-center mb-8"
                   
                  >
                    {headerLabel}
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
                    {products
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((product) => (
                      <div
                        key={product.id}
                        className="flex flex-col items-center group cursor-pointer transition-transform hover:scale-105"
                        onClick={() => navigate(`/product/${product.id}`)}
                      >
                        <div className="relative w-full flex justify-center">
                          <img
                            src={product.image_nobg}
                            alt={product.name}
                            className="h-64 object-contain rounded-lg shadow-md"
                          />
                          <div className="absolute top-2 left-2 flex flex-wrap gap-2">
                            {product.discount != null && product.discount > 0 && (
                              <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">
                                -{product.discount}%
                              </span>
                            )}
                            {product.new && (
                              <span className="bg-black text-white text-xs px-2 py-1 rounded">
                                {tl('Nouveau', 'New', 'Nuovo')}
                              </span>
                            )}
                            {product.collection_2026 && (
                              <span className="bg-furniture-gold text-white text-xs font-bold px-2 py-1 rounded">
                                {tl('Collection 2026', '2026 Collection', 'Collezione 2026')}
                              </span>
                            )}
                          </div>
                          {product.featured && (
                            <span className="absolute top-2 right-2 bg-furniture-wine text-white text-xs px-2 py-1 rounded">
                              {tl('Tendance', 'In Trend', 'Di tendenza')}
                            </span>
                          )}
                        </div>
                        <div className="mt-4 text-center font-medium text-lg" 
                         >
                          {product.name}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
};

const CategorySkeleton = () => {
  return (
    <>
      <div className="bg-muted py-16">
        <div className="container-custom">
          <Skeleton className="h-12 w-1/3 mx-auto mb-4" />
          <Skeleton className="h-4 w-1/2 mx-auto" />
        </div>
      </div>
      
      <div className="container-custom py-12">
        <div className="flex justify-between items-center mb-8">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-10 w-40" />
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="space-y-4">
              <Skeleton className="w-full h-72 rounded-lg" />
              <Skeleton className="h-5 w-2/3" />
              <Skeleton className="h-4 w-1/3" />
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default CategoryPage;
